import { Button } from '@protoss/ui';
import * as React from 'react';
import { AiChatPane } from '../../ai-chat/components/AiChatPane';
import { ChatConnectionIndicator } from '../../ai-chat/components/ChatConnectionIndicator';
import { SessionControls, SessionTitleEditable } from '../../ai-chat/components/SessionControls';
import type { UseChatSessionReturn } from '../../ai-chat/hooks/useChatSession';
import type { ChatPolicy } from '../../ai-chat/types/chatPolicy';
import type { ContextDocsPaneHandle } from '../../context-docs/components/ContextDocsPane';
import { ContextDocsPane } from '../../context-docs/components/ContextDocsPane';
import type { DataCatalogSettingsPaneHandle } from '../../data-catalog/components/DataCatalogSettingsPane';
import { DataCatalogSettingsPane } from '../../data-catalog/components/DataCatalogSettingsPane';
import type { SourceSystemSettingsPaneHandle } from '../../ingestion/components/SourceSystemSettingsPane';
import { SourceSystemSettingsPane } from '../../ingestion/components/SourceSystemSettingsPane';
import type { SourceType } from '../../ingestion/types/source-system';
import { JobLogViewer } from '../../jobs/components/JobLogViewer';
import type { JobLogEntry } from '../../jobs/types/jobs';
import { LiveMeetingPane } from '../../live-meeting/components/LiveMeetingPane';
import { CalendarSettingsPane } from '../../meetings/components/CalendarSettingsPane';
import type { ProjectResourcesPanelHandle } from '../../project-resources/components/ProjectResourcesPanel';
import { ProjectResourcesPanel } from '../../project-resources/components/ProjectResourcesPanel';

// ────────────────────────────────────────────────────────────────────────────
// PanelContext - all runtime state required by any right-rail panel renderer
// ────────────────────────────────────────────────────────────────────────────

export interface PanelContext {
  /** Current project UUID (or undefined if project is loading) */
  projectUuid: string | undefined;

  /** Whether a project is loaded and ready */
  projectReady: boolean;

  /** Active left-nav menu item key (e.g. 'repos', 'commits', 'overview') */
  activeMenuItem: string;

  /**
   * Source type derived from the active menu item.
   * e.g. 'repo' for repos/commits, 'board' for boards, 'pipeline' for pipelines.
   */
  sourceType: SourceType;

  /** Whether the current user can manage integrations (admin/manager) */
  canManageIntegrations: boolean;

  /** Whether the current user can manage project resources (admin/manager) */
  canManageResources: boolean;

  // ── AI chat ──────────────────────────────────────────────────────────────
  chatSession: UseChatSessionReturn;
  chatPolicy: ChatPolicy;

  // ── Sync log ─────────────────────────────────────────────────────────────
  syncLogs: JobLogEntry[];
  syncLogsHasMore: boolean;
  syncLogsLoading: boolean;
  syncLogsLoadMore: () => void;

  // ── Context docs ─────────────────────────────────────────────────────────
  contextDocsPaneRef: React.RefObject<ContextDocsPaneHandle>;
  contextDocsIsEditing: boolean;
  setContextDocsIsEditing: (editing: boolean) => void;

  // ── Source system settings ────────────────────────────────────────────────
  settingsPaneRef: React.RefObject<SourceSystemSettingsPaneHandle>;
  settingsIsEditing: boolean;
  setSettingsIsEditing: (editing: boolean) => void;
  onSettingsSaved: () => void;

  // ── Data catalog settings ──────────────────────────────────────────────────
  dataCatalogSettingsPaneRef: React.RefObject<DataCatalogSettingsPaneHandle>;
  dataCatalogSettingsIsEditing: boolean;
  setDataCatalogSettingsIsEditing: (editing: boolean) => void;
  onDataCatalogSettingsSaved: () => void;

  // ── Project resources ─────────────────────────────────────────────────────
  resourcesPaneRef: React.RefObject<ProjectResourcesPanelHandle>;
}

// ────────────────────────────────────────────────────────────────────────────
// PanelRenderResult - what each panel renderer produces
// ────────────────────────────────────────────────────────────────────────────

export interface PanelRenderResult {
  /** Main panel body content */
  content: React.ReactNode;
  /** Optional right-side action in the pane header */
  headerAction?: React.ReactNode;
  /** Optional override for the pane header's left-side title (defaults to the rail label) */
  headerTitle?: React.ReactNode;
  /** Whether the panel is currently in an editing/creation state (shows badge) */
  isEditing?: boolean;
  /**
   * Optional suffix to append to the DB base label.
   * Final label = `${dbLabel} · ${labelSuffix}` when non-null/non-empty.
   */
  labelSuffix?: string;
  /**
   * Additional runtime enabled check.
   * When false, the panel is hidden even if the DB says it's entitled.
   * e.g. AI chat is disabled until the project has loaded.
   */
  enabled?: boolean;
  /**
   * When true, removes outer padding from the shell wrapper (uses --fill variant).
   * The panel component is responsible for its own scroll and internal spacing.
   * Use for full-height interactive panels (chat, docs, resources, meetings).
   */
  noPadding?: boolean;
}

// Placeholder shown while project is loading
function LoadingPlaceholder({ title }: { title: string }) {
  return (
    <div className="project-right-pane-placeholder">
      <p className="project-right-pane-placeholder__title">{title}</p>
      <p className="project-right-pane-placeholder__body">Loading project…</p>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Panel registry
//
// Maps panelKey → a render function that receives PanelContext and returns
// PanelRenderResult. Content is always React nodes; no DB data goes here.
// ────────────────────────────────────────────────────────────────────────────

type PanelRenderer = (ctx: PanelContext) => PanelRenderResult;

export const PANEL_REGISTRY: Record<string, PanelRenderer> = {
  /**
   * AI chat - always visible, disabled until project has loaded.
   * Header: active session title (left) + SessionControls (history / new).
   */
  chat: (ctx) => ({
    enabled: ctx.projectReady,
    headerTitle: ctx.projectReady ? (
      <SessionTitleEditable
        sessions={ctx.chatSession.state.sessions}
        activeSessionUuid={ctx.chatSession.state.activeSessionUuid}
        onRename={ctx.chatSession.renameSession}
      />
    ) : undefined,
    content: ctx.projectReady ? (
      <AiChatPane
        state={ctx.chatSession.state}
        isConnected={ctx.chatSession.isConnected}
        isRunActive={ctx.chatSession.isRunActive}
        sendRun={ctx.chatSession.sendRun}
        cancelRun={ctx.chatSession.cancelRun}
        confirmRun={ctx.chatSession.confirmRun}
        denyRun={ctx.chatSession.denyRun}
        respondPrompt={ctx.chatSession.respondPrompt}
        retryRun={ctx.chatSession.retryRun}
        updateContext={ctx.chatSession.updateContext}
        setDraftExplicitContext={ctx.chatSession.setDraftExplicitContext}
        confirmDraftExplicitContext={ctx.chatSession.confirmDraftExplicitContext}
        clearDraftExplicitContext={ctx.chatSession.clearDraftExplicitContext}
        removeExplicitContext={ctx.chatSession.removeExplicitContext}
        clearExplicitContext={ctx.chatSession.clearExplicitContext}
        loadOlderHistory={ctx.chatSession.loadOlderHistory}
        setComposerMode={ctx.chatSession.setComposerMode}
        projectUuid={ctx.projectUuid!}
        pageScope={ctx.activeMenuItem}
        onActionSettled={ctx.chatSession.setMessageActionResult}
      />
    ) : (
      <LoadingPlaceholder title="AI" />
    ),
    headerAction: ctx.projectReady ? (
      <div className="chat-header-action">
        <ChatConnectionIndicator
          connectionStatus={ctx.chatSession.state.connectionStatus}
          reconnectAttempt={ctx.chatSession.state.reconnectAttempt}
          onReconnect={ctx.chatSession.manualReconnect}
        />
        <SessionControls
          sessions={ctx.chatSession.state.sessions}
          sessionsStatus={ctx.chatSession.state.sessionsStatus}
          activeSessionUuid={ctx.chatSession.state.activeSessionUuid}
          sessionPool={ctx.chatSession.state.sessionPool}
          onLoad={ctx.chatSession.loadSessions}
          onSwitch={ctx.chatSession.switchSession}
          onCreate={ctx.chatSession.createNewSession}
        />
      </div>
    ) : undefined,
    labelSuffix: ctx.projectReady ? 'Project' : undefined,
    noPadding: true,
  }),

  /**
   * Sync log - shows live job log for the active source system.
   * Label suffix reflects which system is displayed.
   */
  'sync-log': (ctx) => ({
    content: (
      <JobLogViewer
        logs={ctx.syncLogs}
        hasMore={ctx.syncLogsHasMore}
        loading={ctx.syncLogsLoading}
        onLoadMore={ctx.syncLogsLoadMore}
        variant="inline"
      />
    ),
    labelSuffix: systemLabelSuffix(ctx.activeMenuItem),
  }),

  /**
   * Context docs - context document editor for the active source system.
   * Header: New button to create a context document.
   */
  'context-docs': (ctx) => ({
    isEditing: ctx.contextDocsIsEditing,
    content: ctx.projectReady ? (
      <ContextDocsPane
        ref={ctx.contextDocsPaneRef}
        projectUuid={ctx.projectUuid!}
        scope={ctx.activeMenuItem}
        onEditorModeChange={ctx.setContextDocsIsEditing}
      />
    ) : (
      <LoadingPlaceholder title="Context" />
    ),
    headerAction: !ctx.contextDocsIsEditing ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => ctx.contextDocsPaneRef.current?.startNew()}
      >
        New
      </Button>
    ) : undefined,
    labelSuffix: systemLabelSuffix(ctx.activeMenuItem),
    noPadding: true,
  }),

  /**
   * Source system settings - integration configuration pane.
   * Header: Edit button to enter edit mode.
   */
  settings: (ctx) => ({
    isEditing: ctx.settingsIsEditing,
    content: ctx.projectReady ? (
      <SourceSystemSettingsPane
        ref={ctx.settingsPaneRef}
        projectUuid={ctx.projectUuid!}
        sourceType={ctx.sourceType}
        onEditModeChange={ctx.setSettingsIsEditing}
        onSaved={ctx.onSettingsSaved}
      />
    ) : null,
    headerAction: !ctx.settingsIsEditing ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => ctx.settingsPaneRef.current?.enterEdit()}
      >
        Edit
      </Button>
    ) : undefined,
    labelSuffix: systemLabelSuffix(ctx.activeMenuItem),
  }),

  /**
   * Data catalog settings - Databricks catalog scope configuration.
   * Header: Edit button to enter edit mode.
   */
  'data-catalog-settings': (ctx) => ({
    isEditing: ctx.dataCatalogSettingsIsEditing,
    content: ctx.projectReady ? (
      <DataCatalogSettingsPane
        ref={ctx.dataCatalogSettingsPaneRef}
        projectUuid={ctx.projectUuid!}
        onEditModeChange={ctx.setDataCatalogSettingsIsEditing}
        onSaved={ctx.onDataCatalogSettingsSaved}
      />
    ) : null,
    headerAction: !ctx.dataCatalogSettingsIsEditing ? (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => ctx.dataCatalogSettingsPaneRef.current?.enterEdit()}
      >
        Edit
      </Button>
    ) : undefined,
    labelSuffix: 'Databricks',
  }),

  /**
   * Project resources - detected platforms and frameworks from repo scans.
   * Disabled until the project has loaded so we have a valid UUID.
   */
  'project-resources': (ctx) => ({
    enabled: ctx.projectReady,
    content: ctx.projectReady ? (
      <ProjectResourcesPanel
        ref={ctx.resourcesPaneRef}
        projectUuid={ctx.projectUuid!}
        canManage={ctx.canManageResources}
      />
    ) : (
      <LoadingPlaceholder title="Resources" />
    ),
    headerAction:
      ctx.projectReady && ctx.canManageResources ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => ctx.resourcesPaneRef.current?.openAddPicker()}
        >
          Add
        </Button>
      ) : undefined,
    noPadding: true,
  }),

  /**
   * Live meeting intelligence - Zoom RTMS real-time transcript and recommendations.
   * Self-contained: sources workspaceUuid from WorkspaceContext internally.
   */
  'live-meeting': (_ctx) => ({
    content: <LiveMeetingPane />,
    noPadding: true,
  }),

  /**
   * Meetings calendar settings - per-user Google Calendar OAuth connection.
   * Self-contained: manages its own connection state internally.
   */
  'meetings-settings': (_ctx) => ({
    content: <CalendarSettingsPane />,
  }),
};

// ────────────────────────────────────────────────────────────────────────────
// ────────────────────────────────────────────────────────────────────────────

const SYSTEM_LABEL_SUFFIXES: Partial<Record<string, string>> = {
  repos: 'Repos',
  commits: 'Commits',
  boards: 'Boards',
  pipelines: 'Pipelines',
  'data-catalog': 'Data Catalog',
};

function systemLabelSuffix(activeMenuItem: string): string | undefined {
  return SYSTEM_LABEL_SUFFIXES[activeMenuItem];
}
